/**
 * Editor command pattern. Pure: `apply(plan, command)` returns a new plan and
 * the inverse command. No Dexie writes here — the editor module orchestrates
 * apply → push to undo stack → autosave.
 *
 * Identity discipline:
 *   - Every plan vends new building ids and inventory ids from a single
 *     monotonic counter, `Plan.nextEntityId`. This avoids any collision with
 *     FoE's positive `CityMapData[*].id` values, including the negative
 *     synthetic ids that the import parser uses for placements that lack one.
 *   - Commands accept optional `assignBuildingId` / `assignInventoryId` hints.
 *     When apply generates an inverse, it embeds the actual assigned ids so
 *     redo / undo can reproduce them exactly. Without this, round-trip would
 *     drift on every cycle.
 *   - `nextEntityId` is monotonic across undo too; we don't roll it back. The
 *     resulting plan after apply→undo therefore equals the input plan modulo
 *     the counter (and timestamps the storage layer mints). The round-trip
 *     test asserts equality on `buildings` + `inventory`, not the counter.
 */
import type { GridCoord, InventoryItem, Plan, PlanBuilding } from '$lib/types'

export type PlaceSource =
  | { from: 'inventory'; inventoryId: number }
  | { from: 'palette'; cityentityId: string; level?: number }

export type Command =
  | {
      kind: 'move'
      id: number
      from: GridCoord
      to: GridCoord
    }
  | {
      kind: 'remove'
      id: number
      /** Inverse-only hint: re-create the inventory item with this id. */
      assignInventoryId?: number
    }
  | {
      kind: 'place'
      source: PlaceSource
      at: GridCoord
      /** Inverse-only hint: assign the new building this id. */
      assignBuildingId?: number
    }
  | {
      kind: 'duplicate'
      sourceId: number
      at: GridCoord
      /** Inverse-only hint: assign the new building this id. */
      assignBuildingId?: number
    }
  | {
      /**
       * Permanently remove an item from the inventory. Distinct from `remove`
       * (grid → inventory): this one is inventory → nothing. Reusing `remove`
       * would conflate the two with a flag and split the inverse logic in
       * unhelpful ways.
       */
      kind: 'discardFromInventory'
      inventoryId: number
      /**
       * Inverse-only hint: when set, apply re-adds this exact `InventoryItem`
       * snapshot instead of removing one. Captured at apply-time so undo can
       * reconstruct id, cityentityId, level, and source byte-for-byte.
       */
      restore?: InventoryItem
      /**
       * Inverse-only hint: index to re-insert the restored item at, so undo
       * preserves the original ordering. Falls back to append when omitted
       * (which only happens if undo is called against a plan whose inventory
       * has shrunk past the original index).
       */
      restoreAt?: number
    }
  | {
      /**
       * Bulk-place buildings in a single command. Used by the road tool's
       * paint stroke so 50 road tiles undo as one operation. Each placement
       * creates a new building with a fresh id; if the apply-time plan
       * already has a building at the target tile (e.g. another road), the
       * caller is expected to have filtered that out — the apply is honest
       * about creating duplicates so the inverse can remove exactly what
       * was added.
       */
      kind: 'placeMany'
      placements: ReadonlyArray<{ cityentityId: string; at: GridCoord }>
      /**
       * Inverse-only hint: assign these specific ids on apply (used during
       * redo so the undo stack reproduces ids exactly). Length must match
       * `placements`; entries are paired by index.
       */
      assignBuildingIds?: ReadonlyArray<number>
    }
  | {
      /** Inverse of `placeMany`. Removes every listed id; non-existent ids
       *  are silently ignored so a stale inverse doesn't throw. */
      kind: 'removeMany'
      ids: ReadonlyArray<number>
    }
  | {
      /**
       * Bulk-discard inventory items in a single command. The InventoryTab
       * dialog dispatches this when a player clicks "Remove all N" on a
       * stacked row — undo restores the entire stack at the original
       * indices in one operation rather than N separate steps.
       */
      kind: 'discardManyFromInventory'
      inventoryIds: ReadonlyArray<number>
      /**
       * Inverse-only hint: rebuild the stack with each item at its
       * original index. Captured at apply-time so undo round-trips.
       */
      restorations?: ReadonlyArray<{ item: InventoryItem; restoreAt: number }>
    }
  | {
      /**
       * Move multiple placed buildings to inventory in a single command.
       * Used by the store-tool sweep (drag across buildings to bulk-store)
       * and the "Clear all buildings" panel button. Each removed building
       * becomes a fresh inventory item; undo restores all buildings + drops
       * the inventory items in one operation.
       *
       * Inverse appends the restored buildings to the buildings array
       * rather than re-inserting at the original index — building array
       * order doesn't affect rendering (each building draws at its own
       * x/y), so set-equality is the round-trip property we preserve.
       */
      kind: 'removeManyToInventory'
      ids: ReadonlyArray<number>
      /** Inverse-only hint: re-create each building + drop its inventory id. */
      restorations?: ReadonlyArray<{ building: PlanBuilding; inventoryId: number }>
    }

export interface CommandResult {
  newPlan: Plan
  inverse: Command
}

export class CommandError extends Error {
  constructor(
    message: string,
    public readonly code: 'building-not-found' | 'inventory-not-found' | 'no-cityentity-id',
  ) {
    super(message)
    this.name = 'CommandError'
  }
}

/**
 * Apply a command to a plan, returning the new plan and the inverse command
 * that, when applied to the new plan, yields the original (modulo counter +
 * timestamps).
 *
 * Throws `CommandError` only when the command makes no sense (e.g. moving an
 * id that doesn't exist). Validation-style invalid states (overlap, outside
 * unlocked area) are *permitted* — see `validation.ts`.
 */
export function apply(plan: Plan, command: Command): CommandResult {
  switch (command.kind) {
    case 'move':
      return applyMove(plan, command)
    case 'remove':
      return applyRemove(plan, command)
    case 'place':
      return applyPlace(plan, command)
    case 'duplicate':
      return applyDuplicate(plan, command)
    case 'discardFromInventory':
      return applyDiscardFromInventory(plan, command)
    case 'placeMany':
      return applyPlaceMany(plan, command)
    case 'removeMany':
      return applyRemoveMany(plan, command)
    case 'discardManyFromInventory':
      return applyDiscardManyFromInventory(plan, command)
    case 'removeManyToInventory':
      return applyRemoveManyToInventory(plan, command)
  }
}

function applyMove(plan: Plan, command: Extract<Command, { kind: 'move' }>): CommandResult {
  const idx = plan.buildings.findIndex((b) => b.id === command.id)
  if (idx === -1) {
    throw new CommandError(`No building with id ${command.id}`, 'building-not-found')
  }
  const original = plan.buildings[idx]
  const moved: PlanBuilding = { ...original, x: command.to.x, y: command.to.y }
  const buildings = plan.buildings.slice()
  buildings[idx] = moved
  const newPlan: Plan = { ...plan, buildings }
  const inverse: Command = {
    kind: 'move',
    id: command.id,
    from: command.to,
    to: command.from,
  }
  return { newPlan, inverse }
}

function applyRemove(plan: Plan, command: Extract<Command, { kind: 'remove' }>): CommandResult {
  const idx = plan.buildings.findIndex((b) => b.id === command.id)
  if (idx === -1) {
    throw new CommandError(`No building with id ${command.id}`, 'building-not-found')
  }
  const removed = plan.buildings[idx]
  const buildings = plan.buildings.slice()
  buildings.splice(idx, 1)

  const { id: invId, nextEntityId } = vendId(plan, command.assignInventoryId)
  const item: InventoryItem = {
    id: invId,
    cityentityId: removed.cityentityId,
    source: 'placed-then-removed',
  }
  if (typeof removed.level === 'number') item.level = removed.level
  const inventory = [...plan.inventory, item]

  const newPlan: Plan = { ...plan, buildings, inventory, nextEntityId }
  const inverse: Command = {
    kind: 'place',
    source: { from: 'inventory', inventoryId: invId },
    at: { x: removed.x, y: removed.y },
    assignBuildingId: removed.id,
  }
  return { newPlan, inverse }
}

function applyPlace(plan: Plan, command: Extract<Command, { kind: 'place' }>): CommandResult {
  const source = command.source
  let cityentityId: string
  let level: number | undefined
  let inventory = plan.inventory

  if (source.from === 'inventory') {
    const targetId = source.inventoryId
    const invIdx = plan.inventory.findIndex((i) => i.id === targetId)
    if (invIdx === -1) {
      throw new CommandError(
        `No inventory item with id ${targetId}`,
        'inventory-not-found',
      )
    }
    const item = plan.inventory[invIdx]
    cityentityId = item.cityentityId
    level = item.level
    inventory = plan.inventory.slice()
    inventory.splice(invIdx, 1)
  } else {
    if (!source.cityentityId) {
      throw new CommandError('Palette place requires a cityentityId', 'no-cityentity-id')
    }
    cityentityId = source.cityentityId
    level = source.level
  }

  const { id: newBuildingId, nextEntityId } = vendId(plan, command.assignBuildingId)
  const building: PlanBuilding = {
    id: newBuildingId,
    cityentityId,
    x: command.at.x,
    y: command.at.y,
  }
  if (typeof level === 'number') building.level = level
  const buildings = [...plan.buildings, building]

  const newPlan: Plan = { ...plan, buildings, inventory, nextEntityId }

  // The remove inverse re-creates the inventory item we consumed (if any) at
  // the same inventoryId, so the round-trip preserves inventory identity.
  // Palette place: undoing simply removes the building. The remove will
  // create an inventory item, which is the desired UX (placed-from-palette
  // and then undone leaves the building in inventory). The inverse remove
  // does not pin the inventoryId; on redo we'll get a fresh one, which is
  // fine because there's no prior inventoryId to preserve.
  const inverse: Command =
    source.from === 'inventory'
      ? {
          kind: 'remove',
          id: newBuildingId,
          assignInventoryId: source.inventoryId,
        }
      : { kind: 'remove', id: newBuildingId }

  return { newPlan, inverse }
}

function applyDuplicate(
  plan: Plan,
  command: Extract<Command, { kind: 'duplicate' }>,
): CommandResult {
  const source = plan.buildings.find((b) => b.id === command.sourceId)
  if (!source) {
    throw new CommandError(
      `No building with id ${command.sourceId} to duplicate`,
      'building-not-found',
    )
  }
  const { id: newBuildingId, nextEntityId } = vendId(plan, command.assignBuildingId)
  const copy: PlanBuilding = {
    id: newBuildingId,
    cityentityId: source.cityentityId,
    x: command.at.x,
    y: command.at.y,
  }
  if (typeof source.level === 'number') copy.level = source.level
  const buildings = [...plan.buildings, copy]
  const newPlan: Plan = { ...plan, buildings, nextEntityId }
  const inverse: Command = { kind: 'remove', id: newBuildingId }
  return { newPlan, inverse }
}

function applyDiscardFromInventory(
  plan: Plan,
  command: Extract<Command, { kind: 'discardFromInventory' }>,
): CommandResult {
  // Inverse path: re-add the captured snapshot. Reached when the user undoes
  // a previous discard (or redoes after that undo).
  if (command.restore) {
    const item = command.restore
    const inventory = plan.inventory.slice()
    const insertAt =
      typeof command.restoreAt === 'number'
        ? Math.min(Math.max(command.restoreAt, 0), inventory.length)
        : inventory.length
    inventory.splice(insertAt, 0, item)
    // Bump the counter past the restored id so future-vended ids never alias
    // the now-restored inventory item.
    const nextEntityId = Math.max(plan.nextEntityId, item.id + 1)
    const newPlan: Plan = { ...plan, inventory, nextEntityId }
    const inverse: Command = { kind: 'discardFromInventory', inventoryId: item.id }
    return { newPlan, inverse }
  }

  // Forward path: remove the item permanently. The inverse embeds the full
  // snapshot AND its original index so undo restores it byte-for-byte at
  // its original position.
  const idx = plan.inventory.findIndex((i) => i.id === command.inventoryId)
  if (idx === -1) {
    throw new CommandError(
      `No inventory item with id ${command.inventoryId}`,
      'inventory-not-found',
    )
  }
  const removed = plan.inventory[idx]
  const inventory = plan.inventory.slice()
  inventory.splice(idx, 1)
  const newPlan: Plan = { ...plan, inventory }
  const inverse: Command = {
    kind: 'discardFromInventory',
    inventoryId: command.inventoryId,
    restore: removed,
    restoreAt: idx,
  }
  return { newPlan, inverse }
}

function applyPlaceMany(
  plan: Plan,
  command: Extract<Command, { kind: 'placeMany' }>,
): CommandResult {
  if (command.placements.length === 0) {
    // Empty stroke. No-op apply with an empty inverse so undo / redo cycle
    // through cleanly.
    return {
      newPlan: plan,
      inverse: { kind: 'removeMany', ids: [] },
    }
  }

  if (
    command.assignBuildingIds &&
    command.assignBuildingIds.length !== command.placements.length
  ) {
    throw new CommandError(
      'placeMany: assignBuildingIds length must match placements length',
      'no-cityentity-id',
    )
  }

  let nextEntityId = plan.nextEntityId
  const newBuildings: PlanBuilding[] = []
  const newIds: number[] = []
  for (let i = 0; i < command.placements.length; i++) {
    const p = command.placements[i]
    const hint = command.assignBuildingIds?.[i]
    const vended = vendId(plan, hint)
    // We're vending multiple ids in one go; advance nextEntityId past each.
    const id = vended.id
    nextEntityId = Math.max(nextEntityId, vended.nextEntityId)
    if (typeof hint !== 'number') nextEntityId = Math.max(nextEntityId, id + 1)
    newBuildings.push({
      id,
      cityentityId: p.cityentityId,
      x: p.at.x,
      y: p.at.y,
    })
    newIds.push(id)
    // Make the running plan reflect the vended id so the next call doesn't
    // reuse it.
    plan = { ...plan, nextEntityId }
  }

  const buildings = [...plan.buildings, ...newBuildings]
  const newPlan: Plan = { ...plan, buildings, nextEntityId }
  const inverse: Command = { kind: 'removeMany', ids: newIds }
  return { newPlan, inverse }
}

function applyRemoveMany(
  plan: Plan,
  command: Extract<Command, { kind: 'removeMany' }>,
): CommandResult {
  if (command.ids.length === 0) {
    return {
      newPlan: plan,
      inverse: { kind: 'placeMany', placements: [] },
    }
  }

  const idsSet = new Set(command.ids)
  const removed: PlanBuilding[] = []
  const buildings: PlanBuilding[] = []
  for (const b of plan.buildings) {
    if (idsSet.has(b.id)) removed.push(b)
    else buildings.push(b)
  }

  // Inverse re-creates each removed building at its original position with
  // the same id. Order matches `removed` (the order they appeared in the
  // original buildings array), which makes round-trip equality stable.
  const placements = removed.map((b) => ({
    cityentityId: b.cityentityId,
    at: { x: b.x, y: b.y },
  }))
  const assignBuildingIds = removed.map((b) => b.id)

  const newPlan: Plan = { ...plan, buildings }
  const inverse: Command = { kind: 'placeMany', placements, assignBuildingIds }
  return { newPlan, inverse }
}

function applyDiscardManyFromInventory(
  plan: Plan,
  command: Extract<Command, { kind: 'discardManyFromInventory' }>,
): CommandResult {
  // Inverse path: re-insert each captured item at its original index.
  // Sorting ascending so insertions don't shift previously-inserted items'
  // positions away from their original slots — see commands.test.ts for the
  // round-trip property.
  if (command.restorations) {
    if (command.restorations.length === 0) {
      return {
        newPlan: plan,
        inverse: { kind: 'discardManyFromInventory', inventoryIds: [] },
      }
    }
    const inventory = plan.inventory.slice()
    let nextEntityId = plan.nextEntityId
    const sorted = [...command.restorations].sort((a, b) => a.restoreAt - b.restoreAt)
    for (const r of sorted) {
      const insertAt = Math.min(Math.max(r.restoreAt, 0), inventory.length)
      inventory.splice(insertAt, 0, r.item)
      nextEntityId = Math.max(nextEntityId, r.item.id + 1)
    }
    const newPlan: Plan = { ...plan, inventory, nextEntityId }
    const inverse: Command = {
      kind: 'discardManyFromInventory',
      inventoryIds: command.restorations.map((r) => r.item.id),
    }
    return { newPlan, inverse }
  }

  // Forward path: remove items by id, capturing each removed item + its
  // pre-removal index for the inverse.
  if (command.inventoryIds.length === 0) {
    return {
      newPlan: plan,
      inverse: { kind: 'discardManyFromInventory', inventoryIds: [] },
    }
  }
  const idsSet = new Set(command.inventoryIds)
  const restorations: { item: InventoryItem; restoreAt: number }[] = []
  const inventory: InventoryItem[] = []
  for (let i = 0; i < plan.inventory.length; i++) {
    const item = plan.inventory[i]
    if (idsSet.has(item.id)) {
      restorations.push({ item, restoreAt: i })
    } else {
      inventory.push(item)
    }
  }
  const newPlan: Plan = { ...plan, inventory }
  const inverse: Command = {
    kind: 'discardManyFromInventory',
    inventoryIds: command.inventoryIds,
    restorations,
  }
  return { newPlan, inverse }
}

function applyRemoveManyToInventory(
  plan: Plan,
  command: Extract<Command, { kind: 'removeManyToInventory' }>,
): CommandResult {
  // Inverse direction: re-add buildings, drop the generated inventory items.
  if (command.restorations) {
    if (command.restorations.length === 0) {
      return {
        newPlan: plan,
        inverse: { kind: 'removeManyToInventory', ids: [] },
      }
    }
    const inventoryIds = new Set(command.restorations.map((r) => r.inventoryId))
    const inventory = plan.inventory.filter((i) => !inventoryIds.has(i.id))
    const buildings = [
      ...plan.buildings,
      ...command.restorations.map((r) => r.building),
    ]
    const newPlan: Plan = { ...plan, buildings, inventory }
    const inverse: Command = {
      kind: 'removeManyToInventory',
      ids: command.restorations.map((r) => r.building.id),
    }
    return { newPlan, inverse }
  }

  // Forward: remove the listed buildings + push each to inventory.
  if (command.ids.length === 0) {
    return {
      newPlan: plan,
      inverse: { kind: 'removeManyToInventory', ids: [] },
    }
  }
  const idsSet = new Set(command.ids)
  const restorations: { building: PlanBuilding; inventoryId: number }[] = []
  const buildings: PlanBuilding[] = []
  let nextEntityId = plan.nextEntityId
  const newInventoryItems: InventoryItem[] = []
  for (const b of plan.buildings) {
    if (!idsSet.has(b.id)) {
      buildings.push(b)
      continue
    }
    const invId = nextEntityId
    nextEntityId += 1
    const item: InventoryItem = {
      id: invId,
      cityentityId: b.cityentityId,
      source: 'placed-then-removed',
    }
    if (typeof b.level === 'number') item.level = b.level
    newInventoryItems.push(item)
    restorations.push({ building: b, inventoryId: invId })
  }
  const inventory = [...plan.inventory, ...newInventoryItems]
  const newPlan: Plan = { ...plan, buildings, inventory, nextEntityId }
  const inverse: Command = {
    kind: 'removeManyToInventory',
    ids: command.ids,
    restorations,
  }
  return { newPlan, inverse }
}

function vendId(plan: Plan, hint: number | undefined): { id: number; nextEntityId: number } {
  if (typeof hint === 'number') {
    // The hint may equal or exceed the current counter (e.g. when redoing a
    // command whose original assigned id has since been surpassed). Bump the
    // counter so future fresh ids never collide with the hinted one.
    return { id: hint, nextEntityId: Math.max(plan.nextEntityId, hint + 1) }
  }
  return { id: plan.nextEntityId, nextEntityId: plan.nextEntityId + 1 }
}
